import React from "react";

import { capitalize } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import FloatingNav, {
  FloatingNavTab,
  FloatingNavTabs,
} from "@app/analytics/components/reports/FloatingNav";
import EmptyTab from "@app/analytics/MeetingReport/components/EmptyTab";
import useMeetingReportSectionScroll from "@app/analytics/MeetingReport/hooks/useMeetingReportSectionScroll";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscriptEditedToast from "@app/analytics/MeetingReport/hooks/useTranscriptEditedToast";
import { MainContentContainer } from "@app/analytics/MeetingReport/styles";
import type { DeepDiveSection } from "@app/analytics/MeetingReport/types";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import { usePageTitle } from "@app/utils/hooks";

import ScrollableDeepDiveContent from "./ScrollableDeepDiveContent";
import TabbedDeepDiveContent from "./TabbedDeepDiveContent";

const UPLOADED_SESSION_SECTIONS: DeepDiveSection[] = [
  "participation",
  "highlights",
];

const ALL_SECTIONS: DeepDiveSection[] = [
  "participation",
  "standings",
  "highlights",
];

const MeetingReportDeepDiveContent = () => {
  usePageTitle("Meeting Report - Deep Dive", "Reports");
  useTranscriptEditedToast();

  const [queryParams] = useSearchParams();
  const { inDesktopView } = useResponsiveView();

  const {
    data: {
      currentUserCanUpgradeForReport,
      isRestrictedReport,
      isUploadedSession,
    },
  } = useSessionMetadata();

  const preselectedSection = queryParams.get("section") as DeepDiveSection;

  const sections = isUploadedSession ? UPLOADED_SESSION_SECTIONS : ALL_SECTIONS;
  const [selectedSection, setSelectedSection] =
    useMeetingReportSectionScroll<DeepDiveSection>({
      defaultSection: "participation",
      preselectedSection,
      sections,
    });

  if (isRestrictedReport) {
    return (
      <EmptyTab
        tab="Deep Dive"
        title="Get unlimited reports"
        canUpgradeCurrentReport={currentUserCanUpgradeForReport}
      />
    );
  }

  const handleSectionChange = (
    _e: React.SyntheticEvent,
    section: DeepDiveSection
  ) => setSelectedSection(section, true);

  return (
    <>
      <FloatingNav>
        <FloatingNavTabs value={selectedSection} onChange={handleSectionChange}>
          {sections.map((section) => (
            <FloatingNavTab
              key={section}
              label={capitalize(section)}
              value={section}
            />
          ))}
        </FloatingNavTabs>
      </FloatingNav>
      <MainContentContainer>
        {inDesktopView ? (
          <ScrollableDeepDiveContent sections={sections} />
        ) : (
          <TabbedDeepDiveContent selectedSection={selectedSection} />
        )}
      </MainContentContainer>
    </>
  );
};

export default MeetingReportDeepDiveContent;
